import type { UserUuid } from "../identity";

// CU-14ygtkjeh2z: mirrors Healthcare's PrescriptionDraftController contract; a draft is never an approved, issued or sent prescription.
export const PRESCRIPTION_DRAFT_STATUS = "draft" as const;

export type PrescriptionModality = "electronic" | "paper";

export const PRESCRIPTION_MODALITIES: { value: PrescriptionModality; label: string }[] = [
	{ value: "electronic", label: "Electronic" },
	{ value: "paper", label: "Paper" },
];

export interface PrescriptionParticulars {
	medicine_name: string | null;
	medicine_strength: string | null;
	medicine_form: string | null;
	route_of_administration: string | null;
	directions_for_use: string | null;
	quantity: number | null;
	number_of_repeats: number | null;
	reason_for_prescribe: string | null;
	prescription_notes: string | null;
}

export type PrescriptionParticularKey = keyof PrescriptionParticulars;

type ParticularRule = { label: string; kind: "text"; max: number; multiline?: boolean } | { label: string; kind: "integer"; min: number; max: number };

// CU-14ygtkjeh2z: the same bounds as PrescriptionDraftService::PARTICULAR_RULES, so the form never offers what Healthcare refuses.
export const PRESCRIPTION_PARTICULAR_RULES: Record<PrescriptionParticularKey, ParticularRule> = {
	medicine_name: { label: "Medicine name", kind: "text", max: 255 },
	medicine_strength: { label: "Strength", kind: "text", max: 100 },
	medicine_form: { label: "Form", kind: "text", max: 100 },
	route_of_administration: { label: "Route of administration", kind: "text", max: 100 },
	directions_for_use: { label: "Directions for use", kind: "text", max: 1000, multiline: true },
	quantity: { label: "Quantity", kind: "integer", min: 1, max: 99999 },
	number_of_repeats: { label: "Number of repeats", kind: "integer", min: 0, max: 99 },
	reason_for_prescribe: { label: "Reason for prescribing", kind: "text", max: 500, multiline: true },
	prescription_notes: { label: "Prescription notes", kind: "text", max: 1000, multiline: true },
};

export const PRESCRIPTION_PARTICULAR_KEYS = Object.keys(PRESCRIPTION_PARTICULAR_RULES) as PrescriptionParticularKey[];

export const emptyPrescriptionParticulars = (): PrescriptionParticulars => ({
	medicine_name: null,
	medicine_strength: null,
	medicine_form: null,
	route_of_administration: null,
	directions_for_use: null,
	quantity: null,
	number_of_repeats: null,
	reason_for_prescribe: null,
	prescription_notes: null,
});

export interface PrescriptionDraft {
	uuid: string;
	patient_uuid: UserUuid;
	status: string;
	modality: PrescriptionModality;
	organisation_uuid: string;
	prescriber_user_uuid: UserUuid;
	prescriber_provider_uuid: string;
	particulars: PrescriptionParticulars;
	revision: number;
	updated_by_user_uuid: UserUuid;
	created_at: string | null;
	updated_at: string | null;
}

export interface CreatePrescriptionDraftRequest {
	client_request_id: string;
	organisation_uuid: string;
	modality: PrescriptionModality;
	particulars: PrescriptionParticulars;
}

export interface UpdatePrescriptionDraftRequest {
	revision: number;
	organisation_uuid?: string;
	modality?: PrescriptionModality;
	particulars?: Partial<PrescriptionParticulars>;
}
